import type { DayOfWeek } from "@shared/types/foyer";
import type {
  Money,
  OfferActionKind,
  OfferFulfilment,
} from "@shared/types/roomOffer";

/**
 * Every word the Offers screen says on its own behalf (`POS-3`).
 *
 * The offers themselves are `LocalizedString`s an operator wrote; this is the
 * chrome around them — the eyebrow, the "2 platser kvar", the empty state — and
 * it is authored by hand rather than pulled from a translation service for the
 * same reason `careCopy` is: there are perhaps thirty strings, they are read at
 * three metres by a guest with a remote, and a machine translation of "Bara 2
 * platser kvar" that lands as "Only 2 places remain" reads as a different
 * product. Held to a contract by `offersCopy.test.ts`.
 *
 * Two locales, because those are the two Foyer actually ships hotels in today.
 * An unknown locale gets English rather than a blank screen — the same
 * fail-visible instinct the knob accessors follow.
 */

export type OffersLocale = "sv" | "en";

/**
 * What a guest is told to ring when the television cannot finish the job.
 *
 * A constant rather than a per-premise field because there is no such field:
 * `PremiseConfig` carries no reception number today, and inventing one here
 * would be a second place an operator has to keep in step with reality. "9" is
 * the house standard the demo property uses. **The rule it serves is the one
 * that matters and is not negotiable: a failure names a next action.** A dead
 * end on a television is worse than an error — a guest cannot open a console,
 * cannot retry with a keyboard, and cannot see what went wrong.
 */
export const RECEPTION_DIAL = "9";

/**
 * Every refusal a guest can meet, as a closed set.
 *
 * The first seventeen are POS-6's own codes, verbatim — `src/server/api/
 * guestRequests.ts` answers `{ error: "<code>" }` from exactly this list, and a
 * client that renders only the ones it expected shows a blank box for the rest.
 * The last two are the television's own and have no server to come from:
 *
 * - `unreachable` — the request went out and nothing came back, twice. The
 *   idempotency key made the retry safe; a third attempt would not.
 * - `unknown` — an answer arrived carrying a code this bundle has never heard
 *   of, which is an older TV against a newer API. It gets the same "ring
 *   reception" sentence rather than nothing, because degrading to silence is
 *   what makes a guest press Confirm five times.
 */
export const OFFER_ERROR_CODES = [
  "slot_taken",
  "slot_unavailable",
  "slot_required",
  "slot_not_bookable",
  "invalid_selection",
  "party_size_invalid",
  "price_currency_mixed",
  "promotion_not_requestable",
  "offer_unavailable",
  "idempotency_key_required",
  "room_unknown",
  "wrong_premise",
  "not_cancellable",
  "invalid_transition",
  "invalid_action",
  "invalid_status",
  "not_found",
  "unreachable",
  "unknown",
] as const;

export type OfferErrorCode = (typeof OFFER_ERROR_CODES)[number];

/** A server answer's `error` string, mapped onto the closed set above. */
export function offerErrorCode(raw: unknown): OfferErrorCode {
  return (OFFER_ERROR_CODES as readonly string[]).includes(raw as string)
    ? (raw as OfferErrorCode)
    : "unknown";
}

export interface OffersCopy {
  /** Heading, when the premise has NOT renamed the screen with `offersTitle`. */
  title: string;
  /** Small caps line above the heading. */
  eyebrow: string;
  /** The feed has not answered yet. */
  loading: string;
  /** The feed answered and nothing is live right now. */
  empty: string;
  /** Kicker over an order's / an `asap` booking's authored lines. */
  itemsTitle: string;
  /** Kicker over a booking's hours. */
  slotsTitle: string;
  /** Kicker over the small print. */
  termsTitle: string;
  /** The badge each card carries, by action kind. */
  kind: Record<OfferActionKind, string>;
  /** Where an order lands. */
  fulfil: Record<OfferFulfilment, string>;
  /** An `asap` booking has no hour to pick. */
  asap: string;
  /** Seats left on one hour — Foyer's own ledger, never the spa's. */
  seatsLeft: (n: number) => string;
  /** That hour is gone. */
  soldOut: string;
  /** Lead-in on a card price when only the lines carry one ("från 95 kr"). */
  from: string;
  /** A line with nothing to pay. */
  free: string;
  /** Hours the detail view did not have room for. */
  moreTimes: (n: number) => string;
  /** Authored lines the detail view did not have room for. */
  moreItems: (n: number) => string;
  /** Remote hints along the foot. */
  hint: {
    move: string;
    open: string;
    back: string;
    /** OK on a picker tile. */
    pick: string;
    /** ◄ ► on a priced line, or on the party-size stepper. */
    step: string;
    /** OK on the confirm button. */
    send: string;
  };

  /* -- the guest flow (POS-7) --------------------------------------------- */

  /**
   * The primary action on a detail page, by what pressing it will actually do.
   *
   * Four words rather than one, because "Beställ" over a housekeeping call and
   * "Boka" over a club sandwich are both small lies, and the button is the last
   * thing a guest reads before the flow starts.
   */
  start: {
    /** A priced order of goods. */
    order: string;
    /** A booking with hours to choose from. */
    book: string;
    /** An `asap` booking with amenities to choose from. */
    ask: string;
    /** An `asap` booking with nothing to choose — one press calls staff. */
    call: string;
  };
  /** Kicker over the picker on the choose step. */
  chooseTitle: string;
  /** The stepper's zero state on a priced line. */
  quantityNone: string;
  /** Label beside the party-size stepper. */
  partyTitle: string;
  /** How many are coming. */
  partySize: (n: number) => string;
  /** How many amenities are ticked. */
  chosenCount: (n: number) => string;
  /** Lead-in on the running total. */
  totalTitle: string;
  /** Move on from the picker to the confirm step. */
  continueLabel: string;
  /** Nothing is ticked yet, so there is nothing to continue with. */
  pickSomething: string;
  /** Heading on the confirm step. */
  confirmTitle: string;
  /** The button that raises the request. */
  confirmButton: string;
  /** In flight. */
  sending: string;
  /**
   * The last honest sentence: what it costs and which room it lands on.
   *
   * Charge-to-room is the only method in the demo (§6), so this is a STATEMENT
   * rather than a question — there is no method picker, because a choice with
   * one option in it is a keypress that teaches a guest nothing.
   */
  chargeRoom: (amount: string, room: string) => string;
  /** …and the same sentence when there is nothing to pay. */
  chargeNone: (room: string) => string;
  /** Stands in for the room number on a television nobody has placed yet. */
  thisRoom: string;
  /** Status headings, once the request exists. */
  statusTitle: {
    pending: string;
    accepted: string;
    completed: string;
    declined: string;
    cancelled: string;
    refunded: string;
  };
  /**
   * …and the sentence under each.
   *
   * `pending` may never say "Bokat": nothing on a television can commit a
   * kitchen, and the hotel has not agreed to anything until staff press Accept.
   */
  statusBody: {
    pending: string;
    accepted: string;
    completed: string;
    declined: string;
    cancelled: string;
    refunded: string;
  };
  /** Withdraw a request nobody has touched yet. */
  cancelLabel: string;
  /** Leave the request page. */
  doneLabel: string;
  /**
   * Every refusal, in words, each naming what to do next.
   *
   * "Något gick fel" is banned — see {@link RECEPTION_DIAL}.
   */
  errors: Record<OfferErrorCode, string>;
  /** Weekday abbreviations, keyed the way `OfferSlot.day` is. */
  weekday: Record<DayOfWeek, string>;
  /** Decimal separator, for the rare priced-to-the-öre line. */
  decimal: string;
}

const SV: OffersCopy = {
  title: "Erbjudanden",
  eyebrow: "Till rummet",
  loading: "Hämtar erbjudanden…",
  empty: "Inga erbjudanden just nu.",
  itemsTitle: "Att välja på",
  slotsTitle: "Lediga tider",
  termsTitle: "Villkor",
  kind: {
    promotion: "Nyhet",
    order: "Beställ",
    booking: "Boka",
  },
  fulfil: {
    deliver: "Levereras till rummet",
    desk: "Hämtas i receptionen",
  },
  asap: "När det passar dig",
  seatsLeft: (n) => (n === 1 ? "1 plats kvar" : `${n} platser kvar`),
  soldOut: "Fullbokat",
  from: "från",
  free: "Ingen kostnad",
  moreTimes: (n) => (n === 1 ? "+ 1 tid till" : `+ ${n} tider till`),
  moreItems: (n) => (n === 1 ? "+ 1 till" : `+ ${n} till`),
  hint: {
    move: "Bläddra",
    open: "Öppna",
    back: "Tillbaka",
    pick: "Välj",
    step: "Antal",
    send: "Bekräfta",
  },
  start: {
    order: "Beställ",
    book: "Välj tid",
    ask: "Välj vad du behöver",
    call: "Ring efter personal",
  },
  chooseTitle: "Vad vill du ha?",
  quantityNone: "0 ×",
  partyTitle: "Hur många är ni?",
  partySize: (n) => (n === 1 ? "1 gäst" : `${n} gäster`),
  chosenCount: (n) => (n === 1 ? "1 vald" : `${n} valda`),
  totalTitle: "Att betala",
  continueLabel: "Fortsätt",
  pickSomething: "Välj något först.",
  confirmTitle: "Stämmer det här?",
  confirmButton: "Bekräfta",
  sending: "Skickar…",
  chargeRoom: (amount, room) => `${amount} läggs på rum ${room}.`,
  chargeNone: (room) => `Ingen kostnad. Förfrågan kommer från rum ${room}.`,
  thisRoom: "rummet",
  statusTitle: {
    pending: "Skickat till receptionen",
    accepted: "Bekräftat",
    completed: "Klart",
    declined: "Receptionen kunde inte ta emot det",
    cancelled: "Avbrutet",
    refunded: "Återbetalt",
  },
  statusBody: {
    // Never "Bokat": staff have not touched it yet, and the hotel has agreed to
    // nothing a television said on its own.
    pending: "Receptionen bekräftar strax.",
    accepted: "Receptionen har tagit emot det.",
    completed: "Det här är genomfört.",
    declined: `Ring receptionen på ${RECEPTION_DIAL} så löser vi det.`,
    cancelled: "Inget är beställt.",
    refunded: "Beloppet har tagits bort.",
  },
  cancelLabel: "Avbryt",
  doneLabel: "Klar",
  errors: {
    slot_taken: "Den tiden är tagen — välj en annan.",
    slot_unavailable: "Den tiden går inte att boka längre — välj en annan.",
    slot_required: "Välj en tid först.",
    slot_not_bookable: `Det här erbjudandet har inga tider — ring receptionen på ${RECEPTION_DIAL}.`,
    invalid_selection: "Något du valt finns inte längre — välj igen.",
    party_size_invalid: "Ange hur många ni är — mellan 1 och 20.",
    price_currency_mixed: `Priset går inte att räkna ihop — ring receptionen på ${RECEPTION_DIAL}.`,
    promotion_not_requestable: `Det här är bara information — ring receptionen på ${RECEPTION_DIAL} om du vill boka.`,
    offer_unavailable: `Erbjudandet gäller inte just nu — ring receptionen på ${RECEPTION_DIAL}.`,
    idempotency_key_required: `Beställningen gick inte fram — ring receptionen på ${RECEPTION_DIAL}.`,
    room_unknown: `TV:n är inte kopplad till något rum — ring receptionen på ${RECEPTION_DIAL}.`,
    wrong_premise: `TV:n är kopplad till ett annat hotell — ring receptionen på ${RECEPTION_DIAL}.`,
    not_cancellable: `Den går inte att avbryta längre — ring receptionen på ${RECEPTION_DIAL}.`,
    invalid_transition: `Förfrågan har redan ändrats — ring receptionen på ${RECEPTION_DIAL}.`,
    invalid_action: `Vi kunde inte göra det — ring receptionen på ${RECEPTION_DIAL}.`,
    invalid_status: `Vi kunde inte göra det — ring receptionen på ${RECEPTION_DIAL}.`,
    not_found: `Vi hittar inte förfrågan — ring receptionen på ${RECEPTION_DIAL}.`,
    unreachable: `Vi når inte receptionen just nu — ring dem på ${RECEPTION_DIAL}.`,
    unknown: `Något stoppade beställningen — ring receptionen på ${RECEPTION_DIAL}.`,
  },
  weekday: {
    mon: "Mån",
    tue: "Tis",
    wed: "Ons",
    thu: "Tors",
    fri: "Fre",
    sat: "Lör",
    sun: "Sön",
  },
  decimal: ",",
};

const EN: OffersCopy = {
  title: "Offers",
  eyebrow: "To your room",
  loading: "Fetching offers…",
  empty: "Nothing on offer right now.",
  itemsTitle: "Choose from",
  slotsTitle: "Available times",
  termsTitle: "Terms",
  kind: {
    promotion: "News",
    order: "Order",
    booking: "Book",
  },
  fulfil: {
    deliver: "Brought to your room",
    desk: "Collect at the desk",
  },
  asap: "Whenever suits you",
  seatsLeft: (n) => (n === 1 ? "1 place left" : `${n} places left`),
  soldOut: "Fully booked",
  from: "from",
  free: "No charge",
  moreTimes: (n) => (n === 1 ? "+ 1 more time" : `+ ${n} more times`),
  moreItems: (n) => (n === 1 ? "+ 1 more" : `+ ${n} more`),
  hint: {
    move: "Browse",
    open: "Open",
    back: "Back",
    pick: "Choose",
    step: "Quantity",
    send: "Confirm",
  },
  start: {
    order: "Order",
    book: "Choose a time",
    ask: "Choose what you need",
    call: "Call for staff",
  },
  chooseTitle: "What would you like?",
  quantityNone: "0 ×",
  partyTitle: "How many of you?",
  partySize: (n) => (n === 1 ? "1 guest" : `${n} guests`),
  chosenCount: (n) => (n === 1 ? "1 chosen" : `${n} chosen`),
  totalTitle: "To pay",
  continueLabel: "Continue",
  pickSomething: "Choose something first.",
  confirmTitle: "Is this right?",
  confirmButton: "Confirm",
  sending: "Sending…",
  chargeRoom: (amount, room) => `${amount} will be charged to room ${room}.`,
  chargeNone: (room) => `No charge. Requested from room ${room}.`,
  thisRoom: "your room",
  statusTitle: {
    pending: "Sent to reception",
    accepted: "Confirmed",
    completed: "Done",
    declined: "Reception could not take this",
    cancelled: "Cancelled",
    refunded: "Refunded",
  },
  statusBody: {
    // Never "Booked": staff have not touched it yet.
    pending: "Reception will confirm shortly.",
    accepted: "Reception has taken it.",
    completed: "This one is all done.",
    declined: `Ring reception on ${RECEPTION_DIAL} and we will sort it out.`,
    cancelled: "Nothing was ordered.",
    refunded: "The amount has been taken off.",
  },
  cancelLabel: "Cancel",
  doneLabel: "Done",
  errors: {
    slot_taken: "That time has just gone — please pick another.",
    slot_unavailable: "That time can no longer be booked — pick another.",
    slot_required: "Pick a time first.",
    slot_not_bookable: `This offer has no times — ring reception on ${RECEPTION_DIAL}.`,
    invalid_selection: "Something you picked is no longer there — pick again.",
    party_size_invalid: "Say how many of you there are — between 1 and 20.",
    price_currency_mixed: `The price will not add up — ring reception on ${RECEPTION_DIAL}.`,
    promotion_not_requestable: `This one is information only — ring reception on ${RECEPTION_DIAL} to book.`,
    offer_unavailable: `This offer is not available right now — ring reception on ${RECEPTION_DIAL}.`,
    idempotency_key_required: `The order did not go through — ring reception on ${RECEPTION_DIAL}.`,
    room_unknown: `This television is not paired to a room — ring reception on ${RECEPTION_DIAL}.`,
    wrong_premise: `This television belongs to another hotel — ring reception on ${RECEPTION_DIAL}.`,
    not_cancellable: `This can no longer be cancelled — ring reception on ${RECEPTION_DIAL}.`,
    invalid_transition: `The request has already moved on — ring reception on ${RECEPTION_DIAL}.`,
    invalid_action: `We could not do that — ring reception on ${RECEPTION_DIAL}.`,
    invalid_status: `We could not do that — ring reception on ${RECEPTION_DIAL}.`,
    not_found: `We cannot find the request — ring reception on ${RECEPTION_DIAL}.`,
    unreachable: `We cannot reach reception right now — ring them on ${RECEPTION_DIAL}.`,
    unknown: `Something stopped the order — ring reception on ${RECEPTION_DIAL}.`,
  },
  weekday: {
    mon: "Mon",
    tue: "Tue",
    wed: "Wed",
    thu: "Thu",
    fri: "Fri",
    sat: "Sat",
    sun: "Sun",
  },
  decimal: ".",
};

export const OFFERS_COPY: Record<OffersLocale, OffersCopy> = { sv: SV, en: EN };

/**
 * The copy pack for a display locale.
 *
 * Matches on the language subtag alone (`sv-SE` → `sv`), because the premise
 * locale list is free text and a region-qualified code must not silently fall
 * out of Swedish. Anything else is English: an unknown locale getting the wrong
 * language is a nuisance, getting a blank screen is a fault.
 */
export function offersCopy(locale: string | undefined): OffersCopy {
  const lang = (locale ?? "").toLowerCase().split(/[-_]/)[0];
  return lang === "sv" ? SV : EN;
}

/**
 * A price, as the guest reads it.
 *
 * Grouped by hand rather than through `Intl.NumberFormat`, deliberately: the
 * floor is Tizen 7.0 / Chromium 94 on panels whose ICU data we do not control,
 * and a grouping that quietly differs between the CMS preview and the
 * television is the kind of thing nobody notices until an operator swears the
 * price is wrong. `currency` is whatever the operator typed ("kr", "€"), so it
 * follows the amount rather than being placed by rule.
 */
export function formatMoney(price: Money, copy: OffersCopy): string {
  const negative = price.amount < 0;
  const whole = Number.isInteger(price.amount);
  const fixed = Math.abs(price.amount).toFixed(whole ? 0 : 2);
  const [int, frac] = fixed.split(".");
  // Non-breaking spaces, written as escapes so the intent survives a
  // copy-paste: a price must never wrap between its thousands, nor away from
  // its currency, in the middle of a card.
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, "\u00a0");
  const amount = frac ? `${grouped}${copy.decimal}${frac}` : grouped;
  return `${negative ? "−" : ""}${amount}\u00a0${price.currency}`.trim();
}

/**
 * One bookable hour, as a line of text.
 *
 * Built from the slot's OWN parts — `day`, `date` and `time` as the server sent
 * them — and never by parsing `start` into a `Date`. A slot start is a local
 * wall clock (`docs/room-offers.md` §4, `OfferSlot`), so handing it to the
 * browser would re-interpret it in the panel's zone and show a guest in a hotel
 * on the wrong side of midnight an hour the hotel never offered.
 */
export function formatSlot(
  slot: { day: DayOfWeek; date: string; time: string },
  copy: OffersCopy
): string {
  return `${formatSlotDay(slot, copy)} · ${slot.time}`;
}

/**
 * Just the DAY of a bookable hour — "Tors 20/8".
 *
 * The picker groups hours by day and heads each row with this, so the hour
 * tiles themselves can say "19:00" and nothing else (`docs/room-offers.md` §2:
 * a generated slot is `{ id, label: "19:00" }` at the picker's boundary). Same
 * rule as its caller: built from the slot's own parts, never from a parsed
 * `Date`.
 */
export function formatSlotDay(
  slot: { day: DayOfWeek; date: string },
  copy: OffersCopy
): string {
  const [, month, dayOfMonth] = slot.date.split("-");
  const day = Number(dayOfMonth);
  const mon = Number(month);
  const date =
    Number.isFinite(day) && Number.isFinite(mon) ? ` ${day}/${mon}` : "";
  return `${copy.weekday[slot.day]}${date}`;
}
